import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaEye, FaSyncAlt, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Button, Modal, Table, Badge, Spinner, Form, InputGroup, Row, Col } from "react-bootstrap";
import { REACT_APP_API_URL } from "../../../../../env";

const CURRENT_USER_ID = 1;

const API = `${REACT_APP_API_URL}api/tax-config`;

const fmtPercent = (v) => (v != null && v !== "" ? `${parseFloat(v).toFixed(2)}%` : "-");
const fmtMoney   = (v) => (v != null && v !== "" ? `₹${parseFloat(v).toFixed(2)}` : "-");

const StatusBadge = ({ row }) => {
  const isClosed = !!row.EffectiveTo && row.EffectiveTo !== "";
  return isClosed
    ? <Badge bg="secondary">Closed</Badge>
    : <Badge bg="success">Open</Badge>;
};

const toInputDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const TaxValueManagement = () => {
  const [revHeads,     setRevHeads]     = useState([]);
  const [revHeadId,    setRevHeadId]    = useState("");
  const [revItems,     setRevItems]     = useState([]);
  const [revItemId,    setRevItemId]    = useState("");
  const [revLoad,      setRevLoad]      = useState(false);
  const [taxValues, setTaxValues] = useState([]);
  const [listLoad,  setListLoad]  = useState(false);
  const [listErr,   setListErr]   = useState(null);
  const [showForm,     setShowForm]     = useState(false);
  const [editingId,    setEditingId]    = useState(null); 
  const [taxTypes,     setTaxTypes]     = useState([]);
  const [taxConfigId,  setTaxConfigId]  = useState("");
  const [taxTypeId,    setTaxTypeId]    = useState("");
  const [effFrom,      setEffFrom]      = useState("");
  const [priceFrom,    setPriceFrom]    = useState("");
  const [priceTo,      setPriceTo]      = useState("");
  const [taxPercnt,    setTaxPercnt]    = useState("");
  const [cgstPercnt,   setCgstPercnt]   = useState("");
  const [sgstPercnt,   setSgstPercnt]   = useState("");
  const [splitTouched, setSplitTouched] = useState(false);
  const [formLoad,     setFormLoad]     = useState(false);
  const [formErr,      setFormErr]      = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoad, setDetailLoad] = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);
  const loadRevHeads = async () => {
    try {
      setRevLoad(true);
      const res = await axios.get(`${API}/revenue-heads`);
      setRevHeads(res.data.data || []);
    } catch {
      setListErr("Could not load revenue heads.");
    } finally {
      setRevLoad(false);
    }
  };
  useEffect(() => { loadRevHeads(); }, []);
  const onRevHeadChange = async (id) => {
    setRevHeadId(id);
    setRevItemId("");
    setRevItems([]);
    setTaxValues([]);
    if (!id) return;

    try {
      setRevLoad(true);
      const res = await axios.get(`${API}/revenue-head-items`, {
        params: { revHeadId: id, revHeadItemId: 0 }
      });
      const items = res.data.data || [];
      if (items.length && items[0].RevHeadItemId != null) {
        setRevItems(items);
      } else {
        setRevItems([]);
        setRevItemId("0");
      }
    } catch {
      setListErr("Could not load units/items for this revenue head.");
    } finally {
      setRevLoad(false);
    }
  };
  const loadTaxValues = async () => {
    if (!revHeadId || revItemId === "") return;
    try {
      setListLoad(true); setListErr(null);
      const res = await axios.get(`${API}/tax-values/history`, {
        params: { revHeadId, revHeadItemId: revItemId }
      });
      setTaxValues((res.data.data || []).filter((r) => r.TaxValDtlsId));
    } catch (err) {
      setListErr(err.response?.data?.message || "Could not load tax values.");
    } finally {
      setListLoad(false);
    }
  };
  useEffect(() => { loadTaxValues();  }, [revHeadId, revItemId]);

  const loadTaxTypes = async () => {
    try {
      const res = await axios.get(`${API}/tax-types`, { params: { revHeadId } });
      setTaxTypes(res.data.data || []);
    } catch {
      setTaxTypes([]);
    }
  };

  const openAdd = async () => {
    setEditingId(null);
    setTaxConfigId(""); setTaxTypeId("");
    setEffFrom(""); setPriceFrom(""); setPriceTo(""); setTaxPercnt("");
    setCgstPercnt(""); setSgstPercnt(""); setSplitTouched(false);
    setFormErr(null);
    await loadTaxTypes();
    setShowForm(true);
  };

  const openEdit = async (row) => {
    setFormErr(null);
    setShowForm(true);
    setFormLoad(true);
    try {
      await loadTaxTypes();
      const res = await axios.get(`${API}/tax-values/${row.TaxValDtlsId}`);
      const data = res.data.data;
      setEditingId(data.TaxValDtlsId);
      setTaxConfigId(String(data.TaxConfigId || ""));
      setTaxTypeId(String(data.TaxTypeId || ""));
      setEffFrom(toInputDate(data.EffectiveFrom));
      setPriceFrom(data.PriceFrom ?? "");
      setPriceTo(data.PriceTo ?? "");
      setTaxPercnt(data.TaxInPercnt ?? "");

      const subTaxes = data.SubTaxes || [];
      const cgst = subTaxes.find((s) => String(s.TaxType).toUpperCase() === "CGST");
      const sgst = subTaxes.find((s) => String(s.TaxType).toUpperCase() === "SGST");
      setCgstPercnt(cgst ? cgst.TaxInPercnt : "");
      setSgstPercnt(sgst ? sgst.TaxInPercnt : "");
      setSplitTouched(!!(cgst || sgst));
    } catch (err) {
      setFormErr(err.response?.data?.message || "Could not load this record.");
    } finally {
      setFormLoad(false);
    }
  };


  const onTaxTypeSelect = (selectedTaxConfigId) => {
    setTaxConfigId(selectedTaxConfigId);
    const match = taxTypes.find((t) => String(t.TaxConfigId) === String(selectedTaxConfigId));
    setTaxTypeId(match ? String(match.TaxTypeId) : "");
  };

  const onTaxPercntChange = (value) => {
    setTaxPercnt(value);
    if (!splitTouched) {
      const half = value === "" ? "" : (parseFloat(value) / 2).toFixed(2);
      setCgstPercnt(half);
      setSgstPercnt(half);
    }
  };
  const onCgstChange = (value) => { setSplitTouched(true); setCgstPercnt(value); };
  const onSgstChange = (value) => { setSplitTouched(true); setSgstPercnt(value); };
  const handleSave = async () => {
    if (!taxConfigId) return setFormErr("Tax type is required");
    if (!effFrom)     return setFormErr("Effective-from date is required");

    const subTaxes = [];
    if (cgstPercnt !== "") subTaxes.push({ subtaxId: 1, taxType: "CGST", taxInPercnt: cgstPercnt });
    if (sgstPercnt !== "") subTaxes.push({ subtaxId: 2, taxType: "SGST", taxInPercnt: sgstPercnt });

    try {
      setFormLoad(true); setFormErr(null);

      if (editingId) {
        await axios.put(`${API}/tax-values/${editingId}`, {
          revHeadId, revHeadItemId: revItemId,
          taxTypeId, taxConfigId, effectiveFrom: effFrom,
          priceFrom, priceTo, taxInPercnt: taxPercnt,
          subTaxes,
          updatedBy: CURRENT_USER_ID
        });
      } else {
        await axios.post(`${API}/tax-values`, {
          revHeadId, revHeadItemId: revItemId,
          effectiveFrom: effFrom,
          createdBy: CURRENT_USER_ID,
          items: [{ taxTypeId, taxConfigId, priceFrom, priceTo, taxInPercnt: taxPercnt }],
          subTaxes
        });
      }

      setShowForm(false);
      loadTaxValues();
    } catch (err) {
      setFormErr(err.response?.data?.message || "Could not save.");
    } finally {
      setFormLoad(false);
    }
  };
  const viewDetail = async (row) => {
    try {
      setDetailLoad(true); setDetailData(null); setShowDetail(true);
      const res = await axios.get(`${API}/tax-values/${row.TaxValDtlsId}`);
      setDetailData(res.data.data);
    } catch (err) {
      setDetailData(null);
    } finally {
      setDetailLoad(false);
    }
  };
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoad(true);
      await axios.delete(`${API}/tax-values/${deleteId}`, {
        params: { updatedBy: CURRENT_USER_ID }
      });
      setDeleteId(null);
      loadTaxValues();
    } catch (err) {
      setListErr(err.response?.data?.message || "Could not delete.");
      setDeleteId(null);
    } finally {
      setDeleteLoad(false);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-body">

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h3 className="mb-0">Tax Value Management</h3>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={loadTaxValues} disabled={listLoad || !revHeadId}>
              <FaSyncAlt className={listLoad ? "fa-spin" : ""} /> Refresh
            </Button>
            <Button variant="primary" onClick={openAdd} disabled={!revHeadId || revItemId === ""}>
              <FaPlus className="me-1" /> Add Tax Value
            </Button>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Label className="fw-semibold">Revenue Head</Form.Label>
            <Form.Select value={revHeadId} onChange={(e) => onRevHeadChange(e.target.value)} disabled={revLoad}>
              <option value="">Select revenue head…</option>
              {revHeads.map((h) => (
                <option key={h.RevHeadId} value={h.RevHeadId}>{h.RevenueHead}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="fw-semibold">Unit/Hotel Name</Form.Label>
            <Form.Select
              value={revItemId}
              onChange={(e) => setRevItemId(e.target.value)}
              disabled={!revHeadId || revLoad || revItems.length === 0}
            >
              {revItems.length === 0 ? (
                <option value={revItemId || ""}>{revHeadId ? "(no sub-items for this head)" : "Select a revenue head first"}</option>
              ) : (
                <>
                  <option value="">Select item…</option>
                  {revItems.map((it) => (
                    <option key={it.RevHeadItemId} value={it.RevHeadItemId}>{it.RevHeadItem}</option>
                  ))}
                </>
              )}
            </Form.Select>
          </Col>
        </Row>

        {listErr && <div className="alert alert-danger py-2">{listErr}</div>}
        <div className="table-responsive">
          <Table bordered hover className="align-middle">
            <thead className="table-dark text-center">
              <tr>
                <th>#</th><th>Tax Type</th><th>Effective From</th><th>Effective To</th>
                <th>Price From</th><th>Price To</th><th>Tax %</th>
                <th>CGST %</th><th>SGST %</th>
                <th>Status</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!revHeadId || revItemId === "" ? (
                <tr><td colSpan={11} className="text-center text-muted py-4">
                  Select a revenue head and item first.
                </td></tr>
              ) : listLoad ? (
                <tr><td colSpan={11} className="text-center py-4">
                  <Spinner animation="border" size="sm" /> Loading...
                </td></tr>
              ) : taxValues.length > 0 ? taxValues.map((r, i) => (
                <tr key={r.TaxValDtlsId}>
                  <td className="text-center">{i + 1}</td>
                  <td>{r.TaxType}</td>
                  <td className="text-center">{r.EffectiveFrom || "-"}</td>
                  <td className="text-center">{r.EffectiveTo || "-"}</td>
                  <td className="text-center">{fmtMoney(r.PriceFrom)}</td>
                  <td className="text-center">{fmtMoney(r.PriceTo)}</td>
                  <td className="text-center fw-semibold text-success">{fmtPercent(r.TaxInPercnt)}</td>
                  <td className="text-center">{fmtPercent(r.CgstPercnt)}</td>
                  <td className="text-center">{fmtPercent(r.SgstPercnt)}</td>
                  <td className="text-center"><StatusBadge row={r} /></td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-1">
                      <Button size="sm" variant="outline-primary" onClick={() => viewDetail(r)} title="View">
                        <FaEye />
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(r)} title="Edit">
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => setDeleteId(r.TaxValDtlsId)} title="Delete">
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={11} className="text-center text-muted py-4">No tax records found.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Tax Value" : "Add Tax Value"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {formLoad && !taxTypes.length ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Tax Type</Form.Label>
                <Form.Select value={taxConfigId} onChange={(e) => onTaxTypeSelect(e.target.value)} disabled={formLoad}>
                  <option value="">Select tax type…</option>
                  {taxTypes.map((t) => (
                    <option key={t.TaxConfigId} value={t.TaxConfigId}>
                      {t.TaxType}{t.EffectiveFrom ? ` (from ${t.EffectiveFrom})` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Effective From</Form.Label>
                <Form.Control
                  type="date"
                  value={effFrom}
                  onChange={(e) => setEffFrom(e.target.value)}
                  disabled={formLoad}
                />
              </Form.Group>

              <Row className="g-2 mb-3">
                <Col>
                  <Form.Label className="fw-semibold">Price From</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number" step="0.01" placeholder="0.00"
                      value={priceFrom} onChange={(e) => setPriceFrom(e.target.value)}
                      disabled={formLoad}
                    />
                  </InputGroup>
                </Col>
                <Col>
                  <Form.Label className="fw-semibold">Price To</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number" step="0.01" placeholder="0.00"
                      value={priceTo} onChange={(e) => setPriceTo(e.target.value)}
                      disabled={formLoad}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Tax %</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number" step="0.01" placeholder="0.00"
                    value={taxPercnt} onChange={(e) => onTaxPercntChange(e.target.value)}
                    disabled={formLoad}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Row className="g-2 mb-2">
                <Col>
                  <Form.Label className="fw-semibold">CGST %</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number" step="0.01" placeholder="0.00"
                      value={cgstPercnt} onChange={(e) => onCgstChange(e.target.value)}
                      disabled={formLoad}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Col>
                <Col>
                  <Form.Label className="fw-semibold">SGST %</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number" step="0.01" placeholder="0.00"
                      value={sgstPercnt} onChange={(e) => onSgstChange(e.target.value)}
                      disabled={formLoad}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Col>
              </Row>

              {formErr && <div className="alert alert-danger py-2 mt-2 mb-0">{formErr}</div>}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={formLoad}>Cancel</Button>
          <Button variant="dark" onClick={handleSave} disabled={formLoad}>
            {formLoad
              ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</>
              : editingId ? "Update Tax Value" : "Add Tax Value"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDetail} onHide={() => setShowDetail(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Tax Value Detail</Modal.Title></Modal.Header>
        <Modal.Body>
          {detailLoad ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : detailData ? (
            <table className="table table-bordered table-striped mb-0">
              <tbody>
                {[
                  ["Tax Value ID",   <code>{detailData.TaxValDtlsId}</code>],
                  ["Revenue Head",   detailData.RevHeadId],
                  ["Revenue Item",   detailData.RevHeadItemId],
                  ["Tax Type",       detailData.TaxTypeId],
                  ["Tax Config ID",  detailData.TaxConfigId],
                  ["Effective From", detailData.EffectiveFrom ? new Date(detailData.EffectiveFrom).toLocaleDateString("en-IN") : "-"],
                  ["Effective To",   detailData.EffectiveTo ? new Date(detailData.EffectiveTo).toLocaleDateString("en-IN") : <Badge bg="success">Open</Badge>],
                  ["Price From",     fmtMoney(detailData.PriceFrom)],
                  ["Price To",       fmtMoney(detailData.PriceTo)],
                  ["Tax %",          <strong className="text-success">{fmtPercent(detailData.TaxInPercnt)}</strong>],
                  ["CGST / SGST",    (detailData.SubTaxes && detailData.SubTaxes.length)
                    ? detailData.SubTaxes.map((s) => `${s.TaxType} ${fmtPercent(s.TaxInPercnt)}`).join("  •  ")
                    : <span className="text-muted">Not split</span>],
                ].map(([k, v]) => (
                  <tr key={k}><th style={{ width: "35%", fontWeight: 500 }}>{k}</th><td>{v}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-muted mb-0">Failed to load details.</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteId} onHide={() => setDeleteId(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete Tax Value?</Modal.Title></Modal.Header>
        <Modal.Body>
          This tax value will be permanently deleted. This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteId(null)} disabled={deleteLoad}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoad}>
            {deleteLoad
              ? <><Spinner animation="border" size="sm" className="me-2" />Deleting...</>
              : "Delete Tax Value"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaxValueManagement;